import { useCallback, useEffect, useState } from 'react';

import { datadogLogs } from '@datadog/browser-logs';
import { yupResolver } from '@hookform/resolvers/yup';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconArrowRight, IconPound } from '@iwoca/icons';
import { GetCompanySearchResponse } from '@iwoca/lapi-client/edge';
import { Input, Link, Message } from '@iwoca/orion';
import { clsx } from 'clsx';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import styles from './GetStartedUK.module.css';
import { schema } from './GetStartedUK.schema';
import {
  fetchGetEmailAvailability,
  useGetApplicationQuery,
  useGetSelectStateQuery,
  useGetStateQuery,
} from '../../../api/lendingApi/lendingApiQueries';
import { getInitialApplicationBodyUK } from '../../../helpers/getApplicationBody';
import { getLatestStepUK } from '../../../helpers/getLatestStep/getLatestStepUK';
import { useOnce } from '../../../hooks/useOnce';
import { useSignupParams } from '../../../hooks/useSignupParams';
import { useSignupRouterUK } from '../../../hooks/useSignupRouter/useSignupRouterUK';
import { useSplitTestContext } from '../../../hooks/useSplitTestContext';
import { TGetStateResponse } from '../../../types';
import { getAmplitudeDefaultEventProperties } from '../../../utils/amplitudeEvents';
import { logErrorToDataLayer } from '../../../utils/googleDataLayer';
import logger from '../../../utils/logger';
import { Button } from '../../Button/Button';
import {
  normaliseLoanDurationToMonths,
  normaliseStepData,
} from '../../helpers/stateNormalisation';
import { GET_STARTED_STEP_ID } from '../../helpers/steps';
import { DuplicateEmailError, useSubmitGetStarted } from '../submitGetStarted';
import LoanDurationUK, { LOAN_DURATION_OPTIONS_UK } from './LoanDurationUK';
import LoanPurposeUK, { LOAN_PURPOSE_OPTIONS_UK } from './LoanPurposeUK';
import { logSessionIdAccess } from '../../../loggers/AccessLog';
import MarketingOptIn from '../MarketingOptIn/MarketingOptIn';

export type TGetStartedDataUK = {
  email: string;
  amount: string;
  loanDuration: (typeof LOAN_DURATION_OPTIONS_UK)[number]['value'];
  loanPurpose: (typeof LOAN_PURPOSE_OPTIONS_UK)[number]['value'];
  loanPurposeDetails?: string;
  marketingOptIn?: boolean;
};

type TDuplicateEmail = {
  email?: string;
  status: boolean;
};

type TProps = {
  company?: GetCompanySearchResponse['data'][number] | null;
  handleStateUpdateError: (error: Error) => void;
};

const GetStarted = ({ company, handleStateUpdateError }: TProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [duplicateEmail, setDuplicateEmail] = useState<TDuplicateEmail>({
    email: '',
    status: false,
  });

  const { affiliateTag, isDirectCustomer, isPartnerCustomer } =
    useSignupParams();

  useOnce({
    fn: () => {
      logSessionIdAccess();
    },
    conditionFn: () => isDirectCustomer,
  });
  const { pathname, search } = useLocation();
  const basePath = isDirectCustomer ? '' : '/partner-signup';
  const linkToLogin = `/login/?redirect_to=${encodeURIComponent(
    `${basePath}${pathname}${search}`,
  )}`;

  const { navigateSignup } = useSignupRouterUK();
  const { hasAIQuestionSignupEnabled } = useSplitTestContext();
  const { submitGetStarted, postGoogleTrackingSuccess } = useSubmitGetStarted();

  const { state } = useGetStateQuery<TGetStateResponse>();
  const { application } = useGetApplicationQuery();

  const { normalisedState } = useGetSelectStateQuery(
    (data) => normaliseStepData(data)[GET_STARTED_STEP_ID],
  );

  const methods = useForm<TGetStartedDataUK>({
    resolver: yupResolver(schema),
    defaultValues: {
      ...normalisedState,
      amount: application?.requested_products?.[0].amount?.toString(),
    },
  });

  const {
    register,
    getValues,
    setValue,
    setFocus,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
  } = methods;

  useEffect(() => {
    tracking.ampli.stepLoaded({
      'Step name': 'Get started',
      ...getAmplitudeDefaultEventProperties(),
    });
  }, []);

  useEffect(() => {
    const navigateToLatestStep = async () => {
      if (!isSubmitted || !state?.state_key || !application) return;

      const newStep = getLatestStepUK({
        newState: state,
        newApplication: application,
        partnerCustomer: isPartnerCustomer,
        companyQuestionsEnabled: isDirectCustomer && hasAIQuestionSignupEnabled,
      });

      setIsSubmitting(true);

      await navigateSignup({ route: newStep });
    };

    void navigateToLatestStep();
  }, [isSubmitted, JSON.stringify(state), application]);

  useEffect(() => {
    if (normalisedState?.email) {
      setValue('email', normalisedState.email, {
        shouldValidate: true,
      });
    }

    if (normalisedState?.marketingOptIn) {
      setValue('marketingOptIn', normalisedState?.marketingOptIn, {
        shouldValidate: true,
      });
    }
  }, [normalisedState]);

  useEffect(() => {
    const amount = application?.requested_products?.[0].amount;
    const loanDuration = application?.requested_products?.[0].duration;
    const loanPurpose = application?.requested_products?.[0].purpose;

    if (amount) {
      setValue('amount', amount.toString(), {
        shouldValidate: true,
      });
    }

    if (loanDuration) {
      setValue('loanDuration', normaliseLoanDurationToMonths(loanDuration), {
        shouldValidate: true,
      });
    }

    if (loanPurpose) {
      setValue('loanPurpose', loanPurpose as TGetStartedDataUK['loanPurpose'], {
        shouldValidate: true,
      });
    }
  }, [application]);

  const formatCurrency = useCallback(() => {
    const amount = watch('amount')?.replace(/[^0-9]+/g, '');
    if (!amount) return '';

    return parseInt(amount).toLocaleString('en-GB');
  }, [watch]);

  const getEmailAvailability = async (email: string) => {
    if (email) {
      try {
        const emailAvailability = await fetchGetEmailAvailability(email);

        // Track when email is duplicated
        if (!emailAvailability) {
          tracking.ampli.duplicateEmail({
            ...getAmplitudeDefaultEventProperties(),
          });
        }

        return emailAvailability;
      } catch (error: unknown) {
        datadogLogs.logger.error(
          'Fetch email availability error',
          { email },
          error as Error,
        );

        setDuplicateEmail({ email, status: false });
      }
    }
  };

  const onSubmit = async (values: TGetStartedDataUK) => {
    // TODO: Add isLoggedIn check when adding back button flow in Direct Signup
    if (duplicateEmail.status) {
      if (duplicateEmail.email !== values.email) {
        const isEmailAvailable = await getEmailAvailability(values.email);

        if (!isEmailAvailable) return setFocus('email');

        setDuplicateEmail({ email: values.email, status: false });
      } else {
        return setFocus('email');
      }
    }

    try {
      setError(false);
      setIsSubmitting(true);

      tracking.ampli.applicationStarted(getAmplitudeDefaultEventProperties());

      const applicationBody = getInitialApplicationBodyUK(
        affiliateTag,
        values.amount,
        values.loanDuration,
        values.loanPurpose,
      );

      const hadCompleteApplication = Boolean(
        application?.requested_products?.[0].amount,
      );

      await submitGetStarted({
        applicationBody,
        hadCompleteApplication,
        values: {
          ...values,
          prefilledCompany: company,
        },
      });

      postGoogleTrackingSuccess(values);

      // redirect will happen in above use effect when application and state refetched.
      setIsSubmitted(true);
    } catch (error: unknown) {
      if (error instanceof DuplicateEmailError) {
        tracking.ampli.duplicateEmail({
          ...getAmplitudeDefaultEventProperties(),
        });
        setDuplicateEmail({ email: error.email, status: true });
        setFocus('email');
        return;
      }

      logErrorToDataLayer('Step Zero - Form submission error');

      if (error instanceof Error) {
        setError(true);
        handleStateUpdateError(error);

        logger.error('Failed to start application', {
          error: error.message,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      {company ? (
        <h1 className={styles.smallHeading}>
          <span className={styles.welcome}>Welcome,</span>
          {company.name}. Let’s begin.
        </h1>
      ) : (
        <h1 className={styles.heading}>Let’s get started</h1>
      )}
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
          <div className={styles.formFields}>
            <div className={styles.emailContainer}>
              <Input
                {...register('email')}
                wrapperClassName={styles.formField}
                className={clsx('fs-mask dd-privacy-mask', {
                  [styles.duplicateEmailError]: duplicateEmail.status,
                })}
                disabled={Boolean(normalisedState?.email)}
                label="What is your business email address?"
                placeholder="firstname@example.com"
                onBlur={async () => {
                  const email = getValues('email');

                  if (duplicateEmail.email === email) return;

                  // Prevent failed network call with invalid email format
                  const validateEmail = await trigger('email');

                  if (!validateEmail) return;

                  const emailAvailable =
                    normalisedState?.email ||
                    (await getEmailAvailability(email));

                  if (emailAvailable !== undefined) {
                    if (duplicateEmail.status && emailAvailable) {
                      tracking.ampli.duplicateEmailChangedToNonDuplicate();
                    }
                    setDuplicateEmail({
                      email,
                      status: !emailAvailable,
                    });
                  }
                }}
                error={errors.email?.message}
                data-testid="email-input"
              />
              {duplicateEmail.status && !errors.email?.message && (
                <p
                  data-testid="duplicate-email-message"
                  className={styles.duplicateEmail}
                >
                  <strong>
                    That email already exists.{' '}
                    <Link
                      size="small"
                      href={linkToLogin}
                      rel="noopener noreferrer"
                    >
                      Log in
                    </Link>
                  </strong>
                </p>
              )}
            </div>
            <Input
              {...register('amount')}
              value={formatCurrency()}
              wrapperClassName={styles.formField}
              iconLeft={<IconPound />}
              label="How much would you like?"
              placeholder="Up to £1,000,000"
              error={errors.amount?.message}
            />
            <LoanDurationUK />
            <LoanPurposeUK />
            {isDirectCustomer && <MarketingOptIn />}
          </div>

          <Button
            className={styles.submit}
            loading={isSubmitting}
            type="submit"
          >
            Get started
            <IconArrowRight />
          </Button>
        </form>
      </FormProvider>
      {error && (
        <Message
          className={styles.error}
          type="error"
          message="We encountered an error while creating your application"
        />
      )}
      {!state && (
        <div className={styles.loginContainer}>
          Already have an account with iwoca?
          <a className={styles.login} href={linkToLogin}>
            Log in
          </a>
        </div>
      )}
    </div>
  );
};

export default GetStarted;
